/**
 * Utility functions for Calendar module
 */

import { format, parse, startOfWeek, getDay } from 'date-fns';
import { dateFnsLocalizer } from 'react-big-calendar';
import { CalendarEvent } from '../../lib/api';
import { csvBlob } from '../../utils/fileDownload';
import { STATUS_COLORS, EVENT_TYPE_BG_COLORS, EVENT_TYPE_LABELS } from './constants';
import { EventsByMonth } from './types';

const locales = {
  'en-US': require('date-fns/locale/en-US'),
};

export const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

export function formatDateTime(datetime: string): string {
  const date = new Date(datetime);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatDate(datetime: string): string {
  const date = new Date(datetime);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function getStatusBadgeClass(status: string): string {
  return STATUS_COLORS[status] || 'bg-gray-100 text-gray-800';
}

export function getEventStyleGetter(event: { resource?: unknown }) {
  const resource = event.resource as { event_type?: string } | undefined;
  const backgroundColor = EVENT_TYPE_BG_COLORS[resource?.event_type || ''] || '#6b7280';
  return {
    style: {
      backgroundColor,
      borderRadius: '4px',
      opacity: 0.8,
      color: 'white',
      border: '0px',
      display: 'block',
    },
  };
}

/**
 * The month an event falls in, as `YYYY-MM` in the viewer's local time. Keys
 * in this form sort in date order as plain text; the heading shown to the user
 * comes from {@link monthHeading}.
 */
export function monthKeyOf(date: Date): string {
  return format(date, 'yyyy-MM');
}

/**
 * The heading for a {@link monthKeyOf} key, e.g. `2026-10` → `October 2026`.
 * @param key - a `YYYY-MM` month key
 * @returns the month name and year
 */
export function monthHeading(key: string): string {
  const [year, month] = key.split('-').map(Number);
  return format(new Date(year, month - 1, 1), 'MMMM yyyy');
}

/**
 * An ISO timestamp as the local `YYYY-MM-DDTHH:mm` a `datetime-local` input
 * shows. Slicing `toISOString()` gave the UTC time instead, so a user in BST
 * who typed 10:00 saw 09:00 (the saved instant was right; the display was not).
 * @param iso - an ISO-8601 timestamp
 * @returns the same instant in the viewer's local time, for the input's value
 */
export function toLocalDateTimeInput(iso: string): string {
  return format(new Date(iso), "yyyy-MM-dd'T'HH:mm");
}

export function groupEventsByMonth(events: CalendarEvent[]): EventsByMonth {
  const groups: EventsByMonth = {};

  events.forEach((event) => {
    const monthKey = monthKeyOf(new Date(event.start_datetime));
    if (!groups[monthKey]) {
      groups[monthKey] = [];
    }
    groups[monthKey].push(event);
  });

  // Sort events within each month by start date
  Object.keys(groups).forEach((key) => {
    groups[key].sort(
      (a, b) => new Date(a.start_datetime).getTime() - new Date(b.start_datetime).getTime()
    );
  });

  return groups;
}

export function filterEvents(
  events: CalendarEvent[],
  activeEventTypes: string[],
  eventPeriod: string
): CalendarEvent[] {
  return events.filter((event) => {
    const typeMatch = activeEventTypes.includes(event.event_type);
    // CR-6436: the supplier filter lives in the sectioned panel for both
    // views and is applied server-side; the old bar's client-side supplier
    // match (which compared against the CUSTOMER company) is gone.

    // Period filtering
    const now = new Date();
    const eventStart = new Date(event.start_datetime);
    const threeMonthsAgo = new Date(now);
    threeMonthsAgo.setMonth(now.getMonth() - 3);
    const sixMonthsAgo = new Date(now);
    sixMonthsAgo.setMonth(now.getMonth() - 6);

    let periodMatch = true;
    if (eventPeriod === 'upcoming') {
      // R-283: an OVERDUE obligation is still outstanding work, so it belongs
      // in "upcoming" — it is the first thing the viewer needs, not the last.
      // Before this, /calendar's default period dropped every past-due row, so
      // the deadlines a supplier was LATE on were the one population the
      // default view could not show. `metadata.is_overdue` is set by the
      // backend on all four synthetic deadline types (NCR containment, NCR
      // CAR, QBR, action due) and on nothing else, so real CalendarEvent rows
      // keep the plain end-date rule.
      const stillOwed = (event as { metadata?: { is_overdue?: boolean } })
        .metadata?.is_overdue === true;
      periodMatch = stillOwed || new Date(event.end_datetime) >= now;
    } else if (eventPeriod === 'last_3_months') {
      periodMatch = eventStart >= threeMonthsAgo;
    } else if (eventPeriod === 'last_6_months') {
      periodMatch = eventStart >= sixMonthsAgo;
    }

    return typeMatch && periodMatch;
  });
}

export function transformEventsForCalendar(events: CalendarEvent[]) {
  return events.map((event) => ({
    id: event.id,
    title: event.title,
    start: new Date(event.start_datetime),
    end: new Date(event.end_datetime),
    resource: event,
    allDay: event.all_day,
  }));
}

/**
 * CR-6435: CSV of the events the panel currently shows - one row per event,
 * the columns a reader needs to act on it.
 */
export function eventsToCsv(events: ReadonlyArray<CalendarEvent>): string {
  const newline = String.fromCharCode(10);
  const esc = (v: unknown) => {
    const text = v === null || v === undefined ? '' : String(v);
    const needsQuotes = text.includes('"') || text.includes(',') || text.includes(newline);
    return needsQuotes ? '"' + text.split('"').join('""') + '"' : text;
  };
  const header = ['Title', 'Type', 'Supplier', 'Start', 'End', 'All day', 'Status', 'Location', 'Description'];
  const rows = events.map((e) => [
    e.title, EVENT_TYPE_LABELS[e.event_type] || e.event_type, e.supplier_name || '',
    e.start_datetime, e.end_datetime, e.all_day ? 'Yes' : 'No', e.status, e.location || '', e.description || '',
  ].map(esc).join(','));
  return [header.join(','), ...rows].join(newline);
}

/** Hand a CSV to the browser as a download, led by a UTF-8 BOM for Excel (CR-8703). */
export function downloadCsv(csv: string, filename: string): void {
  const blob = csvBlob(csv);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
